import { useEffect, useState } from 'react';
import { resolveShippingOptions } from '../api/storeApi';
import { buildShippingLocationPayload, loadDeliveryLocation } from '../utils/deliveryLocation';
import { loadSavedAddresses } from '../utils/checkoutProfile';

/**
 * WHAT DELIVERING ONE OF THIS ROW COSTS, ASKED ONCE PER PAGE.
 *
 * The product page asks `shipping-options` for the delivery tab, and since
 * 2026-09-17 for the line under the price too (DeliveryPromiseLine). Both
 * mount in the same render, so the second joins the first's request instead of
 * making its own; the answer itself is not remembered past the request, so a
 * later page — or a changed location — asks again.
 */
const inFlight = new Map();

function askShippingOptions(salonSlug, body) {
  const key = `${salonSlug}|${JSON.stringify(body)}`;
  let pending = inFlight.get(key);
  if (!pending) {
    pending = Promise.resolve(resolveShippingOptions(salonSlug, body)).finally(() => inFlight.delete(key));
    inFlight.set(key, pending);
  }
  return pending;
}

/** Said on `window` whenever the shopper picks a delivery location, so every reader follows it. */
export const DELIVERY_LOCATION_EVENT = 'stylon:delivery-location';

export function announceDeliveryLocation(location) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(DELIVERY_LOCATION_EVENT, { detail: location }));
}

/**
 * Where the shopper said they want it delivered: the location they last chose,
 * else their first saved address, else nowhere yet.
 */
export function initialDeliveryLocation() {
  const existing = loadDeliveryLocation();
  if (existing && (existing.address || existing.city)) return existing;
  const saved = loadSavedAddresses();
  if (saved.length > 0) {
    const first = saved[0];
    return {
      address: first.address,
      city: first.city || '',
      country: first.country || '',
      buildingNumber: first.buildingNumber || '',
      apartment: first.apartment || '',
      lat: first.lat ?? null,
      lng: first.lng ?? null,
      // Keep the bridge id so checkout highlights THIS saved row, not the
      // MRU by coincidence while shipping a different typed location.
      savedAddressId: first.id,
    };
  }
  return null;
}

/** The resolved shipping options for one of `variantId` to `location`, or null until they answer. */
export default function useShippingOptions(salonSlug, variantId, location) {
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    if (!salonSlug || !variantId) return undefined;
    let cancelled = false;
    askShippingOptions(salonSlug, {
      ...buildShippingLocationPayload(location),
      items: [{ productVariantId: variantId, quantity: 1 }],
    }).then((r) => { if (!cancelled) setResolved(r); });
    return () => { cancelled = true; };
  }, [salonSlug, variantId, location]);
  return resolved;
}
